import { useContext } from 'react';

import { BlueprintInstanceType, PanelItem } from '@rta/rta-blueprint-library';

import { addContextMenu } from '../../context-menu';
import { AppContext } from '@r-app/app-context';
import { DisplayMode } from '@r-core';
import { RenderPanelItem } from './render-panel-item';
import { renderTracking } from '@r-services/render-tracking';
import { Layer } from '@r-core/types';
import { svgPolygonFromVectors } from '../helpers/svg-polygon-from-vectors';
import { convertRect2Vectors } from '@r-core/helpers';

export type RenderPanelItemGroupProps = {
	panelItemGroupId: string;
};

export const RenderPanelItemGroup = (props: RenderPanelItemGroupProps): React.ReactElement => {
	const { controller: { kitchen, navigation }} = useContext(AppContext);
	const refMap = kitchen.getReferenceMap(props.panelItemGroupId);
	// The group can be gone (last item deleted) when a subscription re-renders; skip instead of crashing on the destructure.
	if (!refMap) return <></>;
	const { island, panelItemGroup } = refMap;

	renderTracking.incrementRender('DrawPanelItemGroup');

	const countertopThickness = island.getCountertop().material.thickness;

	// const agBoxVerticalPadding = 1;
	const agBoxHorizontalPadding = 0.05;
	const agBoxHorizontalMin = 1;
	const agBoxCalcWidth = panelItemGroup.maxWidth + (2 * agBoxHorizontalPadding);
	const agBoxWidth = agBoxCalcWidth < agBoxHorizontalMin ? agBoxHorizontalMin : agBoxCalcWidth;
	const agBoxAddedHeight = panelItemGroup.getMaxDistanceAboveCountertop() + countertopThickness;
	let sortedPanelItems = panelItemGroup.items.children;

	const isSleeve = (pi: PanelItem): boolean => pi.item.component.sleeveDepth !== null || !!pi.item.category?.isInsulatedJacket;
	const isTrimKit = (pi: PanelItem): boolean => pi.item.sku?.endsWith('TK') ?? false;

	if (navigation.displayMode === DisplayMode.Manufacturing || navigation.displayMode === DisplayMode.InlineManufacturing) {
		sortedPanelItems = panelItemGroup.items.children.sort((a, b) => {
			const aVent = a.item.category?.isVent ? 0 : 1;
			const bVent = b.item.category?.isVent ? 0 : 1;
			if (aVent !== bVent) return aVent - bVent;
			const aTrim = isTrimKit(a) ? 0 : 1;
			const bTrim = isTrimKit(b) ? 0 : 1;
			if (aTrim !== bTrim) return aTrim - bTrim;
			if (!isSleeve(a) && isSleeve(b)) return -1;
			if (isSleeve(a) && !isSleeve(b)) return 1;
			return 0;
		});
	} else {
		sortedPanelItems = panelItemGroup.items.children.sort((a, b) => {
			const aVent = a.item.category?.isVent ? 0 : 1;
			const bVent = b.item.category?.isVent ? 0 : 1;
			if (aVent !== bVent) return aVent - bVent;
			const aTrim = isTrimKit(a) ? 0 : 1;
			const bTrim = isTrimKit(b) ? 0 : 1;
			if (aTrim !== bTrim) return aTrim - bTrim;
			if (isSleeve(a) && !isSleeve(b)) return -1;
			if (!isSleeve(a) && isSleeve(b)) return 1;
			return 0;
		});
	}

	// translate to the group using center relative to panel
	return <g type="panel-item-group-container">
		{ /* Draw ApplianceGroup Box for Context Menu */ }
		{ svgPolygonFromVectors(convertRect2Vectors({
				center: { x: 0, y: agBoxAddedHeight / -2 },
				height: island.islandSize.height + agBoxAddedHeight,
				width: agBoxWidth,
				layer: Layer.Overlay
			}), {
				opacity: 0,
				...addContextMenu({
					menuKey: BlueprintInstanceType.PanelItemGroup,
					id: panelItemGroup.id,
					center: panelItemGroup.center
				})
			})
		}

		{ sortedPanelItems
			// only render items with the render flag set and are not excluded (spacers always pass)
			.filter((pi) => { return (pi.item.component?.render || pi.item.category?.isSpacer || pi.item.component?.category?.isSpacer) && !pi.item.exclude })
			// run through render enabled panel items
			.map((panelItem: PanelItem) => {
				return <RenderPanelItem key={panelItem.id} panelItemId={panelItem.id}/>;
			})
		}
	</g>;
};
